import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, Plane, ShieldAlert, Syringe, Phone } from "lucide-react";

const RISK_CLR = {
  basso: "bg-emerald-100 text-emerald-800 border-emerald-300",
  medio: "bg-amber-100 text-amber-800 border-amber-300",
  alto: "bg-rose-100 text-rose-800 border-rose-300",
  sconsigliato: "bg-black text-white border-black",
};

export default function StepLavoriEstero({ dvr, data, onChange, aiLoading, setAiLoading }) {
  const d = data || {};
  const s1 = dvr.steps?.step1_dati_generali || {};
  const [paesi, setPaesi] = useState(d._paesi || "");
  const [durata, setDurata] = useState(d._durata || "");
  const [numLav, setNumLav] = useState(d._num_lav || "");
  const [note, setNote] = useState(d._note || "");

  const run = async () => {
    const paesiList = paesi.split(",").map((x) => x.trim()).filter(Boolean);
    if (!paesiList.length) return toast.error("Inserisci almeno un paese");
    setAiLoading(true);
    try {
      const r = await api.post("/dvr/ai/lavori-estero", {
        paesi: paesiList,
        durata_gg: durata,
        numero_lavoratori: numLav,
        tipo_attivita: s1.tipo_attivita,
        note,
      }, { timeout: 120000 });
      onChange({ ...r.data, _paesi: paesi, _durata: durata, _num_lav: numLav, _note: note });
      toast.success("Valutazione lavori all'estero completata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI error");
    }
    setAiLoading(false);
  };

  return (
    <div className="space-y-4" data-testid="step-lavori-estero">
      <p className="text-sm text-muted-foreground">
        Valutazione trasferte/distacchi ex <b>D.Lgs. 81/08 art. 3 c.6</b>. L'AI usa dati Farnesina
        (Viaggiaresicuri.it), Ministero Salute (vaccinazioni), OMS e restituisce profilo rischio
        paese + misure obbligatorie.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-semibold">Paesi (separati da virgola)</label>
          <Input value={paesi} onChange={(e) => setPaesi(e.target.value)} placeholder="es. Marocco, Nigeria" data-testid="le-paesi" />
        </div>
        <div>
          <label className="text-xs font-semibold">Durata media (giorni)</label>
          <Input type="number" value={durata} onChange={(e) => setDurata(e.target.value)} data-testid="le-durata" />
        </div>
        <div>
          <label className="text-xs font-semibold">N° lavoratori</label>
          <Input type="number" value={numLav} onChange={(e) => setNumLav(e.target.value)} data-testid="le-numlav" />
        </div>
      </div>
      <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note: tipo di lavoro (cantiere, ufficio, campo)…" data-testid="le-note" />
      <Button onClick={run} disabled={aiLoading} className="rounded-full" data-testid="le-run-ai">
        {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plane className="w-4 h-4 mr-2" />}
        Valuta trasferte
      </Button>

      {d.paesi_analizzati?.length > 0 && (
        <div className="space-y-3">
          {d.paesi_analizzati.map((p, i) => (
            <div key={`paese-${p.paese || i}`} className="border border-border rounded-md p-4">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold">{p.paese}</h3>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${RISK_CLR[p.livello_rischio_farnesina] || "bg-slate-100"}`}>
                  {p.livello_rischio_farnesina || "—"}
                </span>
              </div>
              {p.rischi_sanitari?.length > 0 && (
                <div className="text-sm mt-2"><b>Rischi sanitari:</b> {p.rischi_sanitari.join(", ")}</div>
              )}
              {p.vaccinazioni_obbligatorie?.length > 0 && (
                <div className="text-sm mt-1 flex items-start gap-1">
                  <Syringe className="w-3.5 h-3.5 mt-0.5 text-rose-600" />
                  <div><b>Vaccinazioni obbligatorie:</b> {p.vaccinazioni_obbligatorie.join(", ")}</div>
                </div>
              )}
              {p.vaccinazioni_raccomandate?.length > 0 && (
                <div className="text-sm mt-1"><b>Vaccinazioni raccomandate:</b> {p.vaccinazioni_raccomandate.join(", ")}</div>
              )}
              {p.profilassi_farmacologica?.length > 0 && (
                <div className="text-sm mt-1"><b>Profilassi:</b> {p.profilassi_farmacologica.join(", ")}</div>
              )}
              {p.rischi_sicurezza?.length > 0 && (
                <div className="text-sm mt-1 flex items-start gap-1">
                  <ShieldAlert className="w-3.5 h-3.5 mt-0.5 text-amber-600" />
                  <div><b>Rischi sicurezza:</b> {p.rischi_sicurezza.join(", ")}</div>
                </div>
              )}
              {p.note_specifiche && <div className="text-xs text-muted-foreground italic mt-1">{p.note_specifiche}</div>}
            </div>
          ))}
        </div>
      )}

      {d.assicurazioni_richieste?.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm">Assicurazioni richieste</h3>
          <ul className="text-sm mt-1 space-y-0.5">
            {d.assicurazioni_richieste.map((a, i) => <li key={`ass-${i}-${(a || "").slice(0, 20)}`}>• {a}</li>)}
          </ul>
        </div>
      )}

      {d.documenti_lavoratore?.length > 0 && (
        <div className="text-xs text-muted-foreground">Documenti richiesti: {d.documenti_lavoratore.join(" · ")}</div>
      )}

      {d.dpi_specifici?.length > 0 && (
        <div className="text-sm"><b>DPI specifici:</b> {d.dpi_specifici.join(", ")}</div>
      )}

      {d.comunicazioni_emergenza && Object.keys(d.comunicazioni_emergenza).length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-md p-3 text-sm">
          <div className="flex items-center gap-1 font-semibold"><Phone className="w-4 h-4" /> Contatti emergenza</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-1">
            {Object.entries(d.comunicazioni_emergenza).map(([k, v]) => (
              <div key={k}><span className="text-[11px] uppercase text-muted-foreground">{k.replace(/_/g, " ")}</span>: {v}</div>
            ))}
          </div>
        </div>
      )}

      {d.formazione_specifica && (
        <div className="text-sm border border-border rounded-md p-3">
          <b>Formazione:</b> {d.formazione_specifica.ore} — {(d.formazione_specifica.contenuti || []).join(", ")}
          <div className="text-[11px] text-muted-foreground">Rif: {d.formazione_specifica.normativa}</div>
        </div>
      )}
      {d.sintesi && <div className="text-sm text-muted-foreground italic">{d.sintesi}</div>}
    </div>
  );
}
